import { mixed, object, string } from "yup";
import { isNil, omit } from "lodash";
import {
  BurglarAlarmValue,
  TypeOfFuelValue,
  VisitorsInAMonthValue,
  UnderwritingPolicyAnswersInput,
  UnderwritingAnswersDetailsInput,
  UnderwritingPolicyAnswerCancellationRenewInput,
  IbhsFortifiedCertification,
  PolicyLevelUnderwritingAnswers,
  UnderwritingQuestion,
  UnitLevelUnderwritingAnswers as UnitLevelAnswers,
} from "@covertree/new-business-graphql";
import { UnderwritingAnswersValues } from "@covertree/new-business-redux";
import { booleanToYesNo } from "@covertree/utils";
import { YesNo, UnitLevelUnderwritingAnswers } from "@covertree/types";

export type UnderwritingQuestionsForm = Partial<{
  [key in
    | keyof Omit<UnderwritingPolicyAnswersInput, "cancellationRenew">
    | keyof UnderwritingPolicyAnswerCancellationRenewInput
    | keyof Omit<UnderwritingAnswersDetailsInput, "burglarAlarm">]:
    | string
    | undefined
    | null;
}> &
  Partial<{
    burglarAlarm: string | undefined | null;
    burglarAlarmValue: string | undefined | null;
    stormMitigationFortifiedValue: string | undefined | null;
  }>;

/**
 * Policy- and unit-level answers as one flat object. `animalBite` and
 * `conviction` are policy-level answers, so the units' same-named ones are dropped.
 */
export const mergeUnderwritingAnswers = (
  policyLevel: Partial<PolicyLevelUnderwritingAnswers> | undefined,
  unitLevel: Partial<UnitLevelAnswers>[]
) =>
  ({
    ...policyLevel,
    ...omit(Object.assign({}, ...unitLevel), ["animalBite", "conviction"]),
  }) as UnderwritingAnswersValues;

const valueToYesNo = (value: boolean | null | undefined) =>
  booleanToYesNo(value ?? false);

const nestedValueToYesNo = (
  parentValue: boolean | null | undefined,
  value: boolean | null | undefined
) =>
  valueToYesNo(parentValue) === YesNo.Yes ? valueToYesNo(value) : undefined;

export const getDefaultUnderwritingQuestionsForm = (
  answers: UnderwritingAnswersValues
): UnderwritingQuestionsForm => ({
  unitIsTied: valueToYesNo(answers.unitIsTied?.value),
  thermoStaticControl: valueToYesNo(answers.thermoStaticControl?.value),
  utilityServices: valueToYesNo(answers.utilityServices?.value),
  propertyWithFireProtection: valueToYesNo(
    answers.propertyWithFireProtection?.value
  ),
  secureRails: valueToYesNo(answers.secureRails?.value),
  animalBite: valueToYesNo(answers.animalBite),
  automaticSprinkler: valueToYesNo(answers.automaticSprinkler?.value),
  conviction: valueToYesNo(answers.conviction),
  cancellationRenew: valueToYesNo(answers.cancellationRenew?.cancellationRenew),
  reasonCancellationRenew:
    answers.cancellationRenew?.reasonCancellationRenew ?? undefined,
  sourceOfHeat: valueToYesNo(answers.supplementalHeating?.sourceOfHeat.value),
  sourceOfHeatInstallation: nestedValueToYesNo(
    answers.supplementalHeating?.sourceOfHeat.value,
    answers.supplementalHeating?.sourceOfHeatInstallation.value
  ),
  typeOfFuel: answers.supplementalHeating?.typeOfFuel.value
    ? TypeOfFuelValue[answers.supplementalHeating?.typeOfFuel.value]
    : undefined,
  pleaseDescribe:
    answers.supplementalHeating?.pleaseDescribe.value ?? undefined,
  unrepairedDamages: valueToYesNo(answers.unrepairedDamages?.value),
  businessOnPremises: valueToYesNo(answers.business?.businessOnPremises.value),
  daycareOnPremises: nestedValueToYesNo(
    answers.business?.businessOnPremises.value,
    answers.business?.daycareOnPremises.value
  ),
  businessEmployeesOnPremises: nestedValueToYesNo(
    answers.business?.businessOnPremises.value,
    answers.business?.businessEmployeesOnPremises.value ?? undefined
  ),
  visitorsInAMonth: answers.business?.visitorsInAMonth.value
    ? VisitorsInAMonthValue[answers.business?.visitorsInAMonth.value]
    : undefined,
  businessDescription:
    answers.business?.businessDescription?.value ?? undefined,
  trampolineLiability: valueToYesNo(
    answers.trampoline?.trampolineLiability.value
  ),
  trampolineSafetyNet: nestedValueToYesNo(
    answers.trampoline?.trampolineLiability.value,
    answers.trampoline?.trampolineSafetyNet.value
  ),
  trampolineLiabilityExtension: nestedValueToYesNo(
    answers.trampoline?.trampolineLiability.value,
    answers.trampoline?.trampolineLiabilityExtension.value
  ),
  swimmingPool: valueToYesNo(answers.swimmingPool?.swimmingPool.value),
  fourFeetFence: nestedValueToYesNo(
    answers.swimmingPool?.swimmingPool.value,
    answers.swimmingPool?.fourFeetFence.value
  ),
  divingBoard: nestedValueToYesNo(
    answers.swimmingPool?.swimmingPool.value,
    answers.swimmingPool?.divingBoard.value
  ),
  divingBoardSlideLiabilityExtension: nestedValueToYesNo(
    answers.swimmingPool?.swimmingPool.value,
    answers.swimmingPool?.divingBoardSlideLiabilityExtension.value
  ),
  mortgage: valueToYesNo(answers.mortgage?.value),
  burglarAlarm: valueToYesNo(
    answers.burglarAlarm?.value && answers.burglarAlarm?.value !== "No"
      ? answers.burglarAlarm?.value in BurglarAlarmValue
      : false
  ),
  burglarAlarmValue:
    answers.burglarAlarm?.value && answers.burglarAlarm?.value !== "No"
      ? BurglarAlarmValue[answers.burglarAlarm?.value]
      : undefined,
  wroughtIron: valueToYesNo(answers.wroughtIron?.value),
  stormMitigationFortified: valueToYesNo(
    answers.stormMitigationFortified?.value &&
      answers.stormMitigationFortified?.value !== "No"
      ? answers.stormMitigationFortified?.value in IbhsFortifiedCertification
      : false
  ),
  stormMitigationFortifiedValue:
    answers.stormMitigationFortified?.value &&
    answers.stormMitigationFortified?.value !== "No"
      ? IbhsFortifiedCertification[answers.stormMitigationFortified?.value]
      : undefined,
  stormMitigationImpactglass: valueToYesNo(
    answers.stormMitigationImpactglass?.value
  ),
  stormMitigationShutters: valueToYesNo(answers.stormMitigationShutters?.value),
});

const yupBoolean = string().oneOf(
  Object.values(YesNo),
  'Value must be either "Yes" or "No"'
);

export const underwritingQuestionsFormValidation = (
  questions: Record<UnitLevelUnderwritingAnswers, UnderwritingQuestion>
) =>
  object<UnderwritingQuestionsForm>({
    unitIsTied: yupBoolean,
    thermoStaticControl: yupBoolean,
    utilityServices: yupBoolean,
    propertyWithFireProtection: yupBoolean,
    secureRails: yupBoolean,
    animalBite: yupBoolean,
    automaticSprinkler: yupBoolean,
    conviction: yupBoolean,
    cancellationRenew: yupBoolean,
    reasonCancellationRenew: string().when("cancellationRenew", {
      is: (cancellationRenew: YesNo) => cancellationRenew === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    sourceOfHeat: yupBoolean,
    sourceOfHeatInstallation: yupBoolean.when("sourceOfHeat", {
      is: (sourceOfHeat: YesNo) => sourceOfHeat === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    typeOfFuel: mixed()
      .oneOf(Object.values(TypeOfFuelValue), "Invalid Type of Fuel value")
      .when("sourceOfHeat", {
        is: (sourceOfHeat: YesNo) => sourceOfHeat === YesNo.Yes,
        then: (schema) => schema.required("This is a required field"),
        otherwise: (schema) => schema.notRequired(),
      }),
    pleaseDescribe: string().when("typeOfFuel", {
      is: (typeOfFuel: TypeOfFuelValue) =>
        typeOfFuel === TypeOfFuelValue.NotListed,
      then: (schema) =>
        schema.required("This is a required field").trim().min(5).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
    unrepairedDamages: yupBoolean,
    businessOnPremises: yupBoolean,
    daycareOnPremises: yupBoolean.when("businessOnPremises", {
      is: (businessOnPremises: YesNo) => businessOnPremises === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    businessEmployeesOnPremises: yupBoolean.when("businessOnPremises", {
      is: (businessOnPremises: YesNo) => businessOnPremises === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    visitorsInAMonth: mixed()
      .oneOf(
        [
          // "Zero" value is not specified in API VisitorsInAMonthValue enum
          "Zero",
          ...Object.values(VisitorsInAMonthValue),
        ],
        "Invalid visitors in a month value"
      )
      .when("businessOnPremises", {
        is: (businessOnPremises: YesNo) => businessOnPremises === YesNo.Yes,
        then: (schema) => schema.required("This is a required field"),
        otherwise: (schema) => schema.notRequired(),
      }),
    businessDescription: string().when("businessOnPremises", {
      is: (businessOnPremises: YesNo) => businessOnPremises === YesNo.Yes,
      then: (schema) => schema.required("This is a required field").max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
    trampolineLiability: yupBoolean,
    trampolineSafetyNet: string().when("trampolineLiability", {
      is: (trampolineLiability: YesNo) => trampolineLiability === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    trampolineLiabilityExtension: yupBoolean.when("trampolineSafetyNet", {
      is: (trampolineSafetyNet: YesNo) => trampolineSafetyNet === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    swimmingPool: yupBoolean,
    fourFeetFence: yupBoolean.when("swimmingPool", {
      is: (swimmingPool: YesNo) => swimmingPool === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    divingBoard: yupBoolean.when("swimmingPool", {
      is: (swimmingPool: YesNo) => swimmingPool === YesNo.Yes,
      then: (schema) => schema.required("This is a required field"),
      otherwise: (schema) => schema.notRequired(),
    }),
    divingBoardSlideLiabilityExtension: yupBoolean.when(
      ["fourFeetFence", "divingBoard"],
      {
        is: (...divingBoardAndFence: (YesNo | undefined)[]) => {
          const divingBoardExtension =
            questions[
              UnitLevelUnderwritingAnswers.DivingBoardSlideLiabilityExtension
            ];

          if (!divingBoardExtension) return false;

          return (
            divingBoardAndFence.every((value) => !isNil(value)) &&
            !divingBoardAndFence.includes(YesNo.No)
          );
        },
        then: (schema) => schema.required("This is a required field"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    mortgage: yupBoolean,
    burglarAlarm: yupBoolean,
    burglarAlarmValue: mixed()
      .oneOf(Object.values(BurglarAlarmValue), "Invalid burglar alarm value")
      .when("burglarAlarm", {
        is: (burglarAlarm: YesNo) => burglarAlarm === YesNo.Yes,
        then: (schema) => schema.required("This is a required field"),
        otherwise: (schema) => schema.notRequired(),
      }),
    wroughtIron: yupBoolean,
    stormMitigationFortified: yupBoolean,
    stormMitigationFortifiedValue: mixed()
      .oneOf(
        Object.values(IbhsFortifiedCertification).filter(
          (option) => option !== IbhsFortifiedCertification.No
        ),
        "Invalid storm mitigation fortified value"
      )
      .when("stormMitigationFortified", {
        is: (stormMitigationFortified: YesNo) =>
          stormMitigationFortified === YesNo.Yes,
        then: (schema) => schema.required("This is a required field"),
        otherwise: (schema) => schema.notRequired(),
      }),
    stormMitigationImpactglass: yupBoolean,
    stormMitigationShutters: yupBoolean,
  });
